
import { Component, computed, effect, input, output, signal, untracked, ViewEncapsulation } from '@angular/core';

/**
 * Reusable Pagination Component for Server-Side Pagination
 *
 * This component manages pagination UI and emits page changes.
 * Parent component is responsible for fetching data from the server.
 *
 * Usage:
 * Example in parent component template:
 *      <ui-pagination
 *       [totalPages]="totalPages()"
 *       [maxVisiblePages]="5"
 *       [showFirstLast]="true"
 *       [showPrevNext]="true"
 *       [disabled]="loading()"
 *       (pageChange)="onPageChange($event)">
 *      </ui-pagination>
 * - Pass totalPages
 * - Listen to pageChange event to fetch new page data
 */
@Component({
  selector: 'ui-pagination',
  imports: [],
  templateUrl: './pagination.component.html',
  styleUrls: ['../../../../../../ui-components/src/lib/_styles/index.scss', './pagination.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class PaginationComponent {
  // Required inputs
  totalPages = input.required<number>(); // Total page count
  // Optional inputs
  currentPage = input<number>(1);
  maxVisiblePages = input<number>(5);
  showFirstLast = input<boolean>(true);
  showPrevNext = input<boolean>(true);
  disabled = input<boolean>(false);
  // Output: emits page number when user navigates
  pageChange = output<number>();

  // Internal state
  private _currentPage = signal(1);
  pageInputValue = signal<number | null>(null);

  visiblePages = computed(() => {
    const current = this._currentPage();
    const total = this.totalPages();
    const maxVisible = this.maxVisiblePages();

    if (total <= maxVisible) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const half = Math.floor(maxVisible / 2);
    let start = Math.max(1, current - half);
    let end = Math.min(total, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  });

  isFirstPage = computed(() => this._currentPage() === 1);
  isLastPage = computed(() => this._currentPage() === this.totalPages());

  constructor() {
    effect(() => {
      if (this.currentPage() !== untracked(() => this._currentPage())) {
        this._currentPage.set(this.currentPage());
      }
    });
  }

  // Public getter
  get page(): number {
    return this._currentPage();
  }

  /**
   * Navigate to a specific page
   * Emits pageChange event - parent should fetch data from server
   */
  goToPage(page: number): void {
    if (this.disabled() || page < 1 || page > this.totalPages() || page === this._currentPage()) {
      return;
    }
    this._currentPage.set(page);
    this.pageChange.emit(page); // Parent component fetches data from server
  }

  goToFirst = () => this.goToPage(1);
  goToPrevious = () => this.goToPage(this._currentPage() - 1);
  goToNext = () => this.goToPage(this._currentPage() + 1);
  goToLast = () => this.goToPage(this.totalPages());

  /**
   * Handle direct page input navigation
   */
  onPageInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D+/g, '');
    if (input.value !== value) {
      input.value = value;
    }
    const pageNumber = value === '' ? null : parseInt(value, 10);
    this.pageInputValue.set(isNaN(pageNumber as number) ? null : pageNumber);
  }

  onPageInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.navigateToInputPage();
    }
  }

  navigateToInputPage(): void {
    const pageNumber = this.pageInputValue();
    if (pageNumber === null) return;

    let clampedPage = pageNumber;
    if (pageNumber < 1) {
      clampedPage = 1;
    } else if (pageNumber > this.totalPages()) {
      clampedPage = this.totalPages();
    }

    this.goToPage(clampedPage);
    this.pageInputValue.set(null); // Clear input after navigation
  }
}
